import { useCallback, useState } from 'react';

import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import type { DateRange, DurationRange, RecordFilters } from '../lib/recordFilters';
import { INITIAL_FILTERS } from '../lib/recordFilters';

interface UseRecordFiltersOptions {
    // Where the date range starts when the page opens, e.g. a month or a year back.
    initialStart: () => Dayjs;
}

/**
 * Filter state for the record tables (Pending, Records). The filter dialog edits a
 * draft; nothing reaches the table until `apply()` (or `search()` for the search
 * box), so the list doesn't reshuffle on every click inside the dialog.
 *
 * `range` is what the page's query fetches; `applied` is what it filters the
 * fetched rows with, in memory.
 */
function useRecordFilters({ initialStart }: UseRecordFiltersOptions) {
    // Draft values, bound to the filter dialog and the search box.
    const [searchString, setSearchString] = useState('');
    const [statusFilter, setStatusFilter] = useState(INITIAL_FILTERS.status);
    const [deviceTypeFilter, setDeviceTypeFilter] = useState(INITIAL_FILTERS.deviceType);
    const [durationRange, setDurationRange] = useState<DurationRange>(INITIAL_FILTERS.durationRange);
    const [labelFilter, setLabelFilter] = useState<string[]>(INITIAL_FILTERS.labels);
    const [startTime, setStartTime] = useState<Dayjs>(initialStart);
    const [endTime, setEndTime] = useState<Dayjs>(() => dayjs());

    // DatePicker onChange: clear resets to the initial-window default (state can't be null).
    const handleStartTimeChange = useCallback(
        (value: Dayjs | null) => {
            if (value === null) setStartTime(initialStart());
            else if (value.isValid()) setStartTime(value);
        },
        [initialStart]
    );
    const handleEndTimeChange = useCallback((value: Dayjs | null) => {
        if (value === null) setEndTime(dayjs());
        else if (value.isValid()) setEndTime(value);
    }, []);

    // Applied values.
    const [applied, setApplied] = useState<RecordFilters>(INITIAL_FILTERS);
    const [range, setRange] = useState<DateRange>(() => ({ start: startTime, end: null }));

    const search = useCallback(() => setApplied((prev) => ({ ...prev, search: searchString })), [searchString]);

    const apply = useCallback(() => {
        setApplied((prev) => ({
            ...prev,
            status: statusFilter,
            deviceType: deviceTypeFilter,
            durationRange,
            labels: labelFilter,
        }));
        setRange({ start: dayjs(startTime), end: dayjs(endTime) });
    }, [statusFilter, deviceTypeFilter, durationRange, labelFilter, startTime, endTime]);

    // Clears the dialog's filters but keeps the dates the user picked.
    const reset = useCallback(() => {
        setStatusFilter(INITIAL_FILTERS.status);
        setDeviceTypeFilter(INITIAL_FILTERS.deviceType);
        setDurationRange(INITIAL_FILTERS.durationRange);
        setLabelFilter(INITIAL_FILTERS.labels);
        setApplied((prev) => ({ ...INITIAL_FILTERS, search: prev.search }));
        setRange({ start: dayjs(startTime), end: dayjs(endTime) });
    }, [startTime, endTime]);

    // After creating a record: it was created "now", so the range must reach now.
    const extendEndToNow = useCallback(() => {
        setEndTime(dayjs());
        setRange((prev) => (prev.end === null ? prev : { ...prev, end: null }));
    }, []);

    return {
        applied,
        range,
        searchString,
        setSearchString,
        statusFilter,
        setStatusFilter,
        deviceTypeFilter,
        setDeviceTypeFilter,
        durationRange,
        setDurationRange,
        labelFilter,
        setLabelFilter,
        startTime,
        setStartTime,
        endTime,
        setEndTime,
        handleStartTimeChange,
        handleEndTimeChange,
        search,
        apply,
        reset,
        extendEndToNow,
    };
}

export type RecordFiltersState = ReturnType<typeof useRecordFilters>;

export default useRecordFilters;
